import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Unlock, Shield, CheckCircle, X } from "lucide-react";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";
import CardSwiper from "@/components/CardSwiper";

export default function CardUnlock() {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleUnlockCard = () => {
    setShowConfirmModal(true);
  };

  const confirmUnlockCard = () => {
    setIsUnlocking(true);
    setShowConfirmModal(false);
    setError("");

    // Simulate API call
    setTimeout(() => {
      navigate("/mycard");
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/mycard")} />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Card Display */}
        <div className="mt-4">
          <CardSwiper userEmail="user@example.com" />
        </div>

        {/* Unlock Card Section */}
        <Card className="mt-6 bg-card/80">
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Icon and Title */}
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-full">
                  <Unlock className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-xl font-semibold">Unlock Your Card</h2>
                <p className="text-sm text-muted-foreground">
                  Enter your PIN to reactivate your card for transactions
                </p>
              </div>

              {/* PIN Input */}
              {false && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Enter Your 4-Digit PIN
                  </label>
                  <Input
                    type="password"
                    placeholder="••••"
                    value={pin}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 4);
                      setPin(value);
                      setError("");
                    }}
                    maxLength={4}
                    className="text-center text-2xl tracking-widest h-14"
                  />
                  {error && (
                    <p className="text-sm text-red-500 text-center">{error}</p>
                  )}
                </div>
              )}

              {/* Security Info */}
              <div className="p-4 bg-blue-900/80 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex gap-3">
                  <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Security Information
                    </p>
                    <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                      <li>• Your PIN is encrypted and secure</li>
                      <li>
                        • After 3 failed attempts, your card will be blocked
                      </li>
                      <li>• Never share your PIN with anyone</li>
                      <li>• Contact support if you forgot your PIN</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Benefits after Unlocking */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium">
                  After unlocking, you can:
                </h3>
                <div className="space-y-2">
                  {[
                    "Make online purchases",
                    "Use at physical stores",
                    "Withdraw cash from ATMs",
                    "Perform contactless payments",
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-sm text-muted-foreground">
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                <Button
                  onClick={handleUnlockCard}
                  // disabled={isUnlocking || pin.length !== 4}
                  className="w-full h-12 bg-green-600 hover:bg-green-700 text-white"
                >
                  {isUnlocking ? "Unlocking Card..." : "Unlock Card Now"}
                </Button>
              </div>

              {/* Forgot PIN Link */}
              {/* <div className="text-center pt-2">
                <button
                  className="text-sm text-primary hover:underline"
                  onClick={() => navigate("/forgot-pin")}
                >
                  Forgot your PIN?
                </button>
              </div> */}
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        {/* <Card className="mt-4 bg-muted/50">
          <CardContent className="p-4">
            <p className="text-xs text-center text-muted-foreground">
              Need help? Contact our support team 24/7 at support@fellascard.com
            </p>
          </CardContent>
        </Card> */}
      </div>

      <BottomBar />

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 p-4">
          <div className="bg-background rounded-[20px] shadow-lg p-6 w-full max-w-[360px]">
            {/* Close Button */}
            <div className="flex justify-end mb-4">
              <button 
                onClick={() => setShowConfirmModal(false)}
                className="p-1 hover:bg-muted rounded-full"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="space-y-6">
              {/* Icon and Title */}
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-full">
                  <Unlock className="w-10 h-10 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold">Confirm Card Unlock</h3>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to unlock your card?
                </p>
              </div>

              {/* Info */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-center">
                  Once unlocked, your card will be active and ready for all transactions.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 h-11"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmUnlockCard}
                  disabled={isUnlocking}
                  className="flex-1 h-11 bg-green-600 hover:bg-green-700 text-white"
                >
                  {isUnlocking ? "Unlocking..." : "Unlock Card"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
